import { motion } from 'framer-motion';
import { ArrowDownLeft, ArrowUpRight, Coins, Eye, Landmark, MessageSquareWarning } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { PurchaseBadge, ReceiptViewer, REASON_LABEL } from '@/components/coins';
import { Button } from '@/components/ui/button';
import type { Purchase, Transaction } from '@/lib/api/types';
import { cn, formatDateTime, formatMoney, timeAgo } from '@/lib/utils';

export function PurchaseRow({ purchase, showUser = false, actions }: { purchase: Purchase; showUser?: boolean; actions?: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const p = purchase;
  return (
    <motion.div layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:px-5">
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-300/30 to-amber-500/30 text-amber-600 dark:text-amber-400">
          <Coins className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-semibold">
            {p.coins_granted ?? p.coins_requested} coins
            {p.coins_granted !== null && p.coins_granted !== p.coins_requested && (
              <span className="text-xs font-medium text-muted-foreground line-through">{p.coins_requested}</span>
            )}
            <span className="text-muted-foreground">·</span>
            <span>{formatMoney(p.amount, p.currency)}</span>
            <PurchaseBadge status={p.status} />
          </p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 truncate text-xs text-muted-foreground">
            {showUser && (
              <Link to={`/admin/users/${p.user_id}`} className="font-semibold text-primary-ink hover:underline">
                @{p.username}
              </Link>
            )}
            <span className="inline-flex items-center gap-1">
              <Landmark className="size-3" /> {p.bank_account.bank_name}
            </span>
            {p.transaction_ref && <span className="font-mono">Ref {p.transaction_ref}</span>}
            <span title={formatDateTime(p.created_at)}>{timeAgo(p.created_at)}</span>
          </p>
          {p.note && <p className="mt-1 text-xs text-muted-foreground">“{p.note}”</p>}
          {p.admin_note && (
            <p
              className={cn(
                'mt-2 inline-flex items-start gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium',
                p.status === 'declined' ? 'bg-rose-500/10 text-rose-700 dark:text-rose-300' : 'bg-secondary text-muted-foreground',
              )}
            >
              <MessageSquareWarning className="mt-px size-3.5 shrink-0" /> {p.admin_note}
            </p>
          )}
        </div>
      </div>
      <div className="flex shrink-0 gap-2 sm:justify-end">
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
          <Eye /> Receipt
        </Button>
        {actions}
      </div>
      <ReceiptViewer purchase={p} open={open} onOpenChange={setOpen} />
    </motion.div>
  );
}

export function TransactionRow({ tx, showUser = false }: { tx: Transaction; showUser?: boolean }) {
  const plus = tx.delta > 0;
  return (
    <div className="flex items-center gap-4 px-4 py-3.5 sm:px-5">
      <span
        className={cn(
          'flex size-10 shrink-0 items-center justify-center rounded-xl',
          plus ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
        )}
      >
        {plus ? <ArrowDownLeft className="size-4" /> : <ArrowUpRight className="size-4" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{REASON_LABEL[tx.reason]}</p>
        <p className="truncate text-xs text-muted-foreground">
          {showUser && (
            <Link to={`/admin/users/${tx.user_id}`} className="font-semibold text-primary-ink hover:underline">
              @{tx.username}{' '}
            </Link>
          )}
          {tx.note ? `${tx.note} · ` : ''}
          {tx.check_id && (
            <Link to={showUser ? `/admin/checks/${tx.check_id}` : `/checks/${tx.check_id}`} className="hover:underline">
              View check ·{' '}
            </Link>
          )}
          <span title={formatDateTime(tx.created_at)}>{timeAgo(tx.created_at)}</span>
        </p>
      </div>
      <div className="text-right">
        <p className={cn('font-bold tabular-nums', plus ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400')}>
          {plus ? '+' : ''}
          {tx.delta}
        </p>
        <p className="text-xs tabular-nums text-muted-foreground">bal. {tx.balance_after}</p>
      </div>
    </div>
  );
}
